import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { leagueFixturesRequest } from '../../../../api/league';
import { getDomesticCup } from '../../../../api/domesticCup';
import { eventKey, gamesFromFixtures, gamesFromStrategy, gamesFromCup, mergeUpcomingGames } from '../../../../service/upcomingFixtures';
import { isMobileOnly } from 'react-device-detect';
import { useDispatch, useSelector } from 'react-redux';
import {
  StyledSection,
  StyledViewMore,
  StyledCard,
  StyledRow,
  StyledCrest,
  StyledCopy,
  StyledName,
  StyledDate,
  StyledTypePill,
  StyledMatchTop,
  StyledMatchBottom,
  StyledEmpty,
} from './styled';
import SectionHeading from '../../../../components/SectionHeading';
import { ReactComponent as ArrowUpRight } from '../../../../assets/img/arrow-up-right.svg';
import { UpcomingCalendarPreloader } from '../../../../components/ContentPreloaders';
import FallbackContentAnimation from '../../../../components/FallbackContentAnimation';
import { calendarMatch, POPUPS } from '../../../../service/constants';
import { leagueSetTeamInfo } from '../../../../redux/actions/League';
import { showMobileSidebarSection } from '../../../../redux/actions/Team';
import { showPopupWindow } from '../../../../redux/actions/Popups';
import { calendarData } from '../../../../redux/selector/League';
import { formatMatchWhen, useDisplayTimezone } from '../../../../service/localTime';

const formatEventDate = (date, timezone, isCup) =>
  formatMatchWhen(date, {
    timezone,
    isCup,
    dateFormat: 'ddd D',
  });

const HomeCalendar = ({
  data,
  timezone,
  isLoading,
  afterDate,
  afterOpponentId,
  teamId,
  limit = 4,
  contained = false,
  horizontal = false,
  title = "Calendar",
}) => {
  useDisplayTimezone();
  const dispatch = useDispatch();
  const fixtures = useSelector(calendarData);
  const [cup, setCup] = useState(null);
  const [ownFixtures, setOwnFixtures] = useState(null);
  // League browsing can overwrite the shared calendar with another division.
  // Keep the strategy schedule scoped to the active club instead.
  useEffect(() => {
    let active = true;
    setOwnFixtures(null);
    if (horizontal && teamId) {
      leagueFixturesRequest().then(async response => {
        if (response.status === 200) {
          const calendar = await response.json();
          if (active) setOwnFixtures(calendar);
        }
      }).catch(() => {});
    }
    return () => {active = false;};
  }, [horizontal, teamId]);
  useEffect(() => {
    let active = true;
    setCup(null);
    if (teamId) {
      getDomesticCup().then(async (response) => {
        if (response.status === 200) {
          const data = await response.json();
          if (active) setCup(data);
        }
      }).catch(() => { /* Keep league and strategy fixtures available. */ });
    }
    return () => { active = false; };
  }, [teamId]);

  const nextFour = useMemo(() => {
    // Merge authoritative cup dates before excluding the Next Match card.
    const merged = mergeUpcomingGames(
      gamesFromFixtures(ownFixtures || fixtures, teamId),
      gamesFromStrategy(data),
      gamesFromCup(cup, teamId)
    );
    return gamesFromStrategy(merged, afterDate, afterOpponentId).slice(0, limit);
  }, [ownFixtures, fixtures, teamId, afterDate, afterOpponentId, data, limit, cup]);

  const openTeam = (id) => {
    dispatch(leagueSetTeamInfo({ _id: id }, isMobileOnly));
    if (isMobileOnly) {
      dispatch(showMobileSidebarSection('teamInfo'));
    } else {
      dispatch(showPopupWindow(POPUPS.TEAM_INFO_POPUP, POPUPS.TEAM_INFO_POPUP, true));
    }
  };

  const waiting = Boolean(isLoading) && nextFour.length === 0;

  return (
    <StyledSection $contained={contained} $horizontal={horizontal}>
      {contained ? <h3>{title}{horizontal && <StyledViewMore to="/league">See all fixtures <ArrowUpRight/></StyledViewMore>}</h3> : <SectionHeading
        title="Calendar"
        action={
          <StyledViewMore to="/league">
            View more
            <ArrowUpRight />
          </StyledViewMore>
        }
      />}
      <FallbackContentAnimation
        loading={waiting}
        fallback={<UpcomingCalendarPreloader style={{ width: '100%', height: contained ? '168px' : '260px' }} />}
      >
        <StyledCard $compact={contained} $horizontal={horizontal}>
          {nextFour.length === 0 ? (
            <StyledEmpty>No upcoming matches</StyledEmpty>
          ) : (
            nextFour.map((event) => {
              const isCup = Number(event.type) === 1;
              return (
                <StyledRow
                  $compact={contained}
                  key={eventKey(event)}
                  type="button"
                  onClick={() => event.team?._id && openTeam(event.team._id)}
                >
                  {horizontal ? (
                    <>
                      <StyledMatchTop>
                        {event.team?.symbol ? (
                          <StyledCrest src={event.team.symbol} alt="" />
                        ) : (
                          <StyledCrest as="span" />
                        )}
                        <StyledName>{event.team?.name}</StyledName>
                      </StyledMatchTop>
                      <StyledMatchBottom>
                        <StyledDate>{formatEventDate(event.date, timezone || fixtures?.timezone, isCup)}</StyledDate>
                        <StyledTypePill>{calendarMatch[event.type] || 'League'}</StyledTypePill>
                      </StyledMatchBottom>
                    </>
                  ) : (
                    <>
                      {event.team?.symbol ? (
                        <StyledCrest src={event.team.symbol} alt="" />
                      ) : (
                        <StyledCrest as="span" />
                      )}
                      <StyledCopy>
                        <StyledName>{event.team?.name}</StyledName>
                        <StyledDate>{formatEventDate(event.date, timezone || fixtures?.timezone, isCup)}</StyledDate>
                      </StyledCopy>
                      <StyledTypePill>{calendarMatch[event.type] || 'League'}</StyledTypePill>
                    </>
                  )}
                </StyledRow>
              );
            })
          )}
        </StyledCard>
      </FallbackContentAnimation>
    </StyledSection>
  );
};

HomeCalendar.propTypes = {
  data: PropTypes.array,
  timezone: PropTypes.string,
  isLoading: PropTypes.bool,
  afterDate: PropTypes.string,
  afterOpponentId: PropTypes.string,
  teamId: PropTypes.string,
};

HomeCalendar.defaultProps = {
  data: [],
  timezone: null,
  isLoading: false,
  afterDate: null,
  afterOpponentId: null,
  teamId: null,
};

export default HomeCalendar;
